// Render an OpenAPI 3.x spec to a full HTML API reference (info, auth, endpoints
// grouped by tag, parameters, request/response schemas + examples, and
// auto-generated cURL/JS/Python samples). Pure string output (no DOM/React) so
// it runs both in the browser editor NodeView and the SSR docs post-process.
// Reuses the .api-endpoint card classes; sample <pre><code> get Shiki-highlighted
// on the docs site.

const HTTP = ['get', 'post', 'put', 'patch', 'delete', 'head', 'options'];

const esc = (s: any) =>
  String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

function resolveRef(spec: any, node: any, depth = 0): any {
  if (!node || typeof node !== 'object' || depth > 20) return node;
  if (typeof node.$ref === 'string') {
    const path = node.$ref.replace(/^#\//, '').split('/');
    let cur = spec;
    for (const p of path) cur = cur?.[p];
    return resolveRef(spec, cur, depth + 1);
  }
  return node;
}

/** Build a representative JSON example from a schema (honours `example`). */
function exampleFromSchema(spec: any, schema0: any, depth = 0): any {
  const schema = resolveRef(spec, schema0, 0);
  if (!schema || typeof schema !== 'object' || depth > 6) return null;
  if (schema.example !== undefined) return schema.example;
  if (schema.default !== undefined) return schema.default;
  if (schema.enum?.length) return schema.enum[0];
  if (Array.isArray(schema.allOf)) return Object.assign({}, ...schema.allOf.map((s: any) => exampleFromSchema(spec, s, depth + 1) || {}));
  const type = schema.type || (schema.properties ? 'object' : undefined);
  switch (type) {
    case 'object': {
      const out: Record<string, any> = {};
      const props = schema.properties || {};
      for (const k of Object.keys(props)) out[k] = exampleFromSchema(spec, props[k], depth + 1);
      return out;
    }
    case 'array': return [exampleFromSchema(spec, schema.items, depth + 1)].filter((v) => v !== null);
    case 'integer': case 'number': return 0;
    case 'boolean': return true;
    case 'string': return schema.format === 'date-time' ? '2026-01-01T00:00:00Z' : 'string';
    default: return null;
  }
}

/** Properties table for an object schema (name/type/required/description). */
function schemaTable(spec: any, schema0: any): string {
  const schema = resolveRef(spec, schema0, 0);
  const props = schema?.properties;
  if (!props || !Object.keys(props).length) return '';
  const required: string[] = schema.required || [];
  const rows = Object.keys(props).map((name) => {
    const p = resolveRef(spec, props[name], 0) || {};
    const type = p.type + (p.type === 'array' && p.items ? `<${resolveRef(spec, p.items, 0)?.type || 'object'}>` : '') + (p.format ? ` (${p.format})` : '');
    return `<tr><td><code>${esc(name)}</code></td><td>${esc(type || 'object')}</td><td>${required.includes(name) ? 'Yes' : 'No'}</td><td>${esc(p.description || '')}</td></tr>`;
  });
  return `<table class="api-table"><thead><tr><th>Field</th><th>Type</th><th>Required</th><th>Description</th></tr></thead><tbody>${rows.join('')}</tbody></table>`;
}

const codeBlock = (lang: string, code: string) => `<pre><code class="language-${lang}">${esc(code)}</code></pre>`;
const pretty = (v: any) => { try { return JSON.stringify(v, null, 2); } catch { return String(v); } };

function samples(method: string, url: string, auth: string, bodyExample: any): string {
  const m = method.toUpperCase();
  const body = bodyExample != null ? pretty(bodyExample) : '';
  const curl = [`curl -X ${m} "${url}"`];
  if (auth) curl.push(`  -H "Authorization: ${auth}"`);
  if (body) { curl.push(`  -H "Content-Type: application/json"`); curl.push(`  -d '${body}'`); }
  const js = [`const res = await fetch("${url}", {`, `  method: "${m}",`];
  const jsH: string[] = [];
  if (auth) jsH.push(`    "Authorization": "${auth}",`);
  if (body) jsH.push(`    "Content-Type": "application/json",`);
  if (jsH.length) js.push(`  headers: {`, ...jsH, `  },`);
  if (body) js.push(`  body: JSON.stringify(${body}),`);
  js.push(`});`, `const data = await res.json();`);
  const py = ['import requests', ''];
  let call = `resp = requests.${m.toLowerCase()}("${url}"`;
  const pyH: string[] = [];
  if (auth) pyH.push(`"Authorization": "${auth}"`);
  if (pyH.length) call += `, headers={${pyH.join(', ')}}`;
  if (body) call += `, json=${body}`;
  py.push(call + ')', 'print(resp.json())');
  const items: [string, string, string][] = [['cURL', 'bash', curl.join(' \\\n')], ['JavaScript', 'javascript', js.join('\n')], ['Python', 'python', py.join('\n')]];
  return `<div class="api-label">Code samples</div><div class="api-samples">${
    items.map(([label, lang, code]) => `<div class="api-sample"><div class="api-sample-lang">${label}</div>${codeBlock(lang, code)}</div>`).join('')
  }</div>`;
}

function authHeader(spec: any): string {
  const schemes = spec.components?.securitySchemes || {};
  for (const s of Object.values<any>(schemes)) {
    if (s?.type === 'http' && s?.scheme === 'bearer') return 'Bearer <token>';
    if (s?.type === 'apiKey' && s?.in === 'header') return `<${s.name}>`;
    if (s?.type === 'oauth2') return 'Bearer <token>';
  }
  return '';
}

/** "Try it" console form for an operation. Gated + wired by the docs site
 *  (hidden unless a proxy allow-list is configured; requests go through /_try). */
function tryItHtml(method: string, url: string, params: any[], bodyExample: any): string {
  const fields = params.map((p) =>
    `<div class="tryit-field"><label>${esc(p.name)} <span class="tryit-in">${esc(p.in)}</span>${p.required ? ' <span class="tryit-req">*</span>' : ''}</label>` +
    `<input class="tryit-param" data-name="${esc(p.name)}" data-in="${esc(p.in)}" placeholder="${esc(p.name)}"></div>`).join('');
  const bodyField = bodyExample != null
    ? `<div class="tryit-field"><label>Request body (JSON)</label><textarea class="tryit-reqbody" rows="5">${esc(pretty(bodyExample))}</textarea></div>`
    : '';
  return `<div class="tryit" data-method="${esc(method.toUpperCase())}" data-url="${esc(url)}">` +
    `<button type="button" class="tryit-toggle">Try it ▸</button>` +
    `<div class="tryit-panel" hidden>${fields}${bodyField}` +
    `<button type="button" class="tryit-send">Send request</button>` +
    `<div class="tryit-response" hidden></div></div></div>`;
}

function operation(spec: any, baseUrl: string, path: string, method: string, op: any): string {
  const auth = op.security?.length !== 0 ? authHeader(spec) : '';
  const parts: string[] = [`<div class="api-endpoint" id="op-${esc(method)}-${esc(path.replace(/[^a-z0-9]+/gi, '-'))}">`];
  parts.push(`<div class="api-head"><span class="api-method m-${method.toLowerCase()}">${method.toUpperCase()}</span><code class="api-path">${esc(baseUrl + path)}</code></div>`);
  if (op.summary) parts.push(`<div class="api-summary">${esc(op.summary)}</div>`);
  if (op.description) parts.push(`<p class="api-desc">${esc(op.description)}</p>`);
  if (auth) parts.push(`<div class="api-auth"><span class="api-auth-label">Auth</span><code>${esc(auth)}</code></div>`);

  const params = (op.parameters || []).map((p: any) => resolveRef(spec, p, 0)).filter(Boolean);
  if (params.length) {
    parts.push(`<div class="api-label">Parameters</div><table class="api-table"><thead><tr><th>Name</th><th>In</th><th>Type</th><th>Required</th><th>Description</th></tr></thead><tbody>${
      params.map((p: any) => `<tr><td><code>${esc(p.name)}</code></td><td>${esc(p.in)}</td><td>${esc(resolveRef(spec, p.schema, 0)?.type || '')}</td><td>${p.required ? 'Yes' : 'No'}</td><td>${esc(p.description || '')}</td></tr>`).join('')
    }</tbody></table>`);
  }

  const reqSchema = op.requestBody?.content?.['application/json']?.schema;
  const reqExample = op.requestBody?.content?.['application/json']?.example ?? (reqSchema ? exampleFromSchema(spec, reqSchema) : null);
  if (reqSchema) {
    parts.push(`<div class="api-label">Request body</div>`);
    const t = schemaTable(spec, reqSchema);
    if (t) parts.push(t);
    if (reqExample != null) parts.push(codeBlock('json', pretty(reqExample)));
  }

  const responses = op.responses || {};
  if (Object.keys(responses).length) {
    parts.push(`<div class="api-label">Responses</div><table class="api-table"><thead><tr><th>Status</th><th>Description</th></tr></thead><tbody>${
      Object.keys(responses).map((code) => `<tr><td><code>${esc(code)}</code></td><td>${esc(resolveRef(spec, responses[code], 0)?.description || '')}</td></tr>`).join('')
    }</tbody></table>`);
    for (const code of Object.keys(responses)) {
      const r = resolveRef(spec, responses[code], 0);
      const sch = r?.content?.['application/json']?.schema;
      const ex = r?.content?.['application/json']?.example ?? (sch ? exampleFromSchema(spec, sch) : null);
      if (ex != null) { parts.push(`<div class="api-label">Example response · ${esc(code)}</div>`); parts.push(codeBlock('json', pretty(ex))); }
    }
  }

  parts.push(samples(method, baseUrl + path, auth, reqExample));
  parts.push(tryItHtml(method, baseUrl + path, params, reqExample));
  parts.push(`</div>`);
  return parts.join('');
}

/** Replace apiSpec markers (<div data-api-spec="…">) in rendered doc HTML with
 *  the full generated reference. Run BEFORE Shiki so sample blocks highlight. */
export function expandApiSpecs(html: string): string {
  if (!html.includes('data-api-spec')) return html;
  return html.replace(/<div[^>]*\bdata-api-spec="([^"]*)"[^>]*>\s*<\/div>/g, (_m, raw) => {
    try {
      const json = String(raw).replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');
      const spec = JSON.parse(json);
      return `<div class="api-spec">${renderOpenApiHtml(spec)}</div>`;
    } catch { return '<div class="oas oas-error">Could not render API spec.</div>'; }
  });
}

export function renderOpenApiHtml(spec: any): string {
  if (!spec || typeof spec !== 'object' || !spec.paths) {
    return `<div class="oas oas-error">Invalid or empty OpenAPI spec.</div>`;
  }
  const info = spec.info || {};
  const baseUrl = spec.servers?.[0]?.url || '';
  const parts: string[] = ['<div class="oas">'];
  parts.push(`<div class="oas-info"><div class="oas-title">${esc(info.title || 'API Reference')} <span class="oas-ver">v${esc(info.version || '')}</span></div>`);
  if (baseUrl) parts.push(`<div class="oas-base"><span class="api-auth-label">Base URL</span> <code>${esc(baseUrl)}</code></div>`);
  if (info.description) parts.push(`<p>${esc(info.description)}</p>`);
  parts.push('</div>');

  // Collect operations, group by first tag.
  const groups = new Map<string, string[]>();
  for (const path of Object.keys(spec.paths)) {
    const item = spec.paths[path] || {};
    for (const method of HTTP) {
      const op = item[method];
      if (!op) continue;
      const tag = (op.tags && op.tags[0]) || 'Endpoints';
      if (!groups.has(tag)) groups.set(tag, []);
      groups.get(tag)!.push(operation(spec, baseUrl, path, method, op));
    }
  }
  for (const [tag, ops] of groups) {
    parts.push(`<h3 class="oas-tag">${esc(tag)}</h3>`);
    parts.push(...ops);
  }
  parts.push('</div>');
  return parts.join('');
}
